import { useTranslation } from 'react-i18next'

import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeaderLeftIcon,
  SheetTitle,
  SheetHeader,
  SheetCustomContent,
  SheetClose,
  SheetFooter,
  ListItem,
  ListItemLeading,
  ListItemMainContainer,
  ListItemMainContent,
  ListItemMainLabel,
  ListItemParagraph,
} from '@sweetspot/scramble-ds'
import { Button } from '@sweetspot/scramble-ds/atoms'

import { useEffect, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useFormattedTimezones } from '../hooks/useFormattedTimezones'
import { PlayerImportSchema } from './validation'
import { useMutation } from 'react-query'
import { importMembershipInvites } from '@sweetspot/shared/data-access/api-platform'
import { useToasts } from 'react-toast-notifications'
import { TimezoneSelector } from '../components/TimezoneSelector'
import { Instructions } from './partials/Instructions'
import { AccordionInstructions, FileSelector } from '../components'
// eslint-disable-next-line @nx/enforce-module-boundaries
import SpinnerLoader from '@sweetspot/club-portal-legacy/components/SpinnerLoader/SpinnerLoader'
import { ImportErrorDialog } from '../components/ImportErrorDialog'
import { SubscriptionType } from '@sweetspot/shared/types'

type PlayerImportFormData = z.infer<typeof PlayerImportSchema>

interface PlayerImportSheetProps {
  subscriptionType: SubscriptionType
}

export const PlayerImportSheet = ({ subscriptionType }: PlayerImportSheetProps) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [error, setError] = useState('')
  const [importFailledModal, setImportFailedModal] = useState(false)
  const { addToast } = useToasts()

  const { t } = useTranslation()
  const { formattedTimezones, userTimezone } = useFormattedTimezones()

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    setValue,
    reset,
  } = useForm<PlayerImportFormData>({
    resolver: zodResolver(PlayerImportSchema),
    defaultValues: {
      file: undefined,
      timezone: undefined,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (userTimezone) {
      setValue('timezone', userTimezone, { shouldDirty: false })
    }
  }, [userTimezone, setValue])

  const playersImportMutation = useMutation(
    (formData: FormData) => importMembershipInvites(formData),
    {
      onSuccess: (data) => {
        addToast(t('memberships.membershipImports.instructions.players.success'), {
          appearance: 'success',
        })
        reset()
        setIsSheetOpen(false)
      },
      onError: (error) => {
        setError(error?.detail)
        setImportFailedModal(true)
      },
    }
  )

  const onSubmit = (data: PlayerImportFormData) => {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('timezone', data.timezone.id)

    setError('')
    playersImportMutation.mutate(formData)
  }

  const isFormFilled = isDirty && isValid

  return (
    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
      <SheetTrigger asChild className="cursor-pointer">
        <ListItem className="text-text-dark w-full min-w-fit cursor-pointer gap-5 px-2 py-3">
          <ListItemLeading className="px-0">
            <i className="fa-regular fa-file-circle-plus" />
          </ListItemLeading>
          <ListItemMainContainer>
            <ListItemMainContent className="py-0">
              <ListItemMainLabel className="h-auto min-h-6 font-bold">
                {t('memberships.membershipImports.heading_player_import')}
              </ListItemMainLabel>
              <ListItemParagraph className="text-text-subtle">
                {t('memberships.membershipImports.description_play_import')}
              </ListItemParagraph>
            </ListItemMainContent>
          </ListItemMainContainer>
        </ListItem>
      </SheetTrigger>
      <SheetContent className="max-w-[480px] sm:max-w-[480px]">
        {playersImportMutation.isLoading && (
          <SpinnerLoader
            className="absolute inset-0 [&_p]:w-[300px]"
            description={t('memberships.membershipImports.loading')}
            isOverlay
          />
        )}
        <SheetHeader>
          <SheetClose asChild>
            <SheetHeaderLeftIcon>
              <i className="fa-regular fa-close" />
            </SheetHeaderLeftIcon>
          </SheetClose>
          <SheetTitle className="text-lg">
            {t('memberships.membershipImports.heading_player_import')}
          </SheetTitle>
        </SheetHeader>
        <SheetCustomContent className="flex flex-col gap-4 ">
          <FileSelector name="file" control={control} maxFileSize={10485760} />
          <AccordionInstructions
            title={t('memberships.membershipImports.instructions.players.title')}
          >
            <Instructions subscriptionType={subscriptionType} />
          </AccordionInstructions>
          <TimezoneSelector
            control={control}
            name="timezone"
            formattedTimezones={formattedTimezones}
          />
        </SheetCustomContent>
        <SheetFooter className="pt-0">
          <Button
            className="w-full"
            variant="primary"
            disabled={!isFormFilled}
            onClick={() => {
              handleSubmit(onSubmit)()
            }}
          >
            {t('words.import')}
          </Button>
        </SheetFooter>
      </SheetContent>
      <ImportErrorDialog
        open={importFailledModal}
        onOpenChange={setImportFailedModal}
        error={error}
        onRetry={() => handleSubmit(onSubmit)()}
        isRetryDisabled={!isFormFilled || playersImportMutation.isLoading}
      />
    </Sheet>
  )
}
